import {
  COMPLETE_IDENTITY_PROFILE,
  COMPLETE_PROFILE_CREATION,
  getAuthSessionActive,
  setAuthSessionActive,
  START_IDENTITY_PROFILE
} from '@local/do-secundo-passwordless-authentication'
import { cache, config, defaultData } from '../../apollo/apollo-client'
import { dataByTypename } from '../../utils/apollo-helpers'
import {
  accountLinkingStorage,
  cleanUpCartData,
  deleteTokens,
  setTokens,
  storage
} from './authentication-helpers'
import {
  CUSTOMER_SERVER,
  LOGIN_SERVER,
  MFA_LOGIN_SERVER
} from './authentication.graphql'
import { ooV2Auth } from './oov2-auth'

export const handleAuthResponse = (response, forAccountLinking = false) => {
  const {
    AuthenticationResponse: data,
    MfaChallengeGeneratedResponse: mfaData,
    LoginError: error
  } = dataByTypename(response)

  if (error) {
    if (forAccountLinking) {
      deleteTokens(accountLinkingStorage)
    } else {
      deleteTokens()
    }
    return error
  }

  if (mfaData) return mfaData

  if (forAccountLinking) {
    setTokens(data, accountLinkingStorage)
  } else {
    setTokens(data)
  }
  return data
}

const getLoginCodeErrorMessage = (message) => {
  if (message.includes('403')) {
    return 'Wrong or expired code. Try again by reentering or resending the code.'
  }

  if (message.includes('429')) {
    return 'You have reached the maximum number of failed attempts. Please verify you have entered a valid sms code and valid phone number.'
  }

  return message
}

const completeIdentityProfile = async (client, input) => {
  const resp = await client.mutate({
    mutation: COMPLETE_IDENTITY_PROFILE,
    variables: {
      input
    }
  })
  const { CompleteIdentityProfileResponse, CompleteIdentityProfileError } =
    dataByTypename(resp.data.completeIdentityProfile)

  if (CompleteIdentityProfileError) {
    return CompleteIdentityProfileError
  }

  return CompleteIdentityProfileResponse
}

export const authenticationResolvers = {
  Query: {
    customer: async (obj, args, { client }) => {
      let token = storage.get('accessToken')
      const isPasswordlessAuthenticated = getAuthSessionActive()
      if (!token && !isPasswordlessAuthenticated) {
        // Fall back to OO V2 if possible
        const ooV2Tokens = await ooV2Auth.getTokens()
        if (ooV2Tokens) {
          setTokens(ooV2Tokens)
          token = storage.get('accessToken')
        }
      }

      if (token || isPasswordlessAuthenticated) {
        try {
          const result = await client.query({
            query: CUSTOMER_SERVER,
            fetchPolicy: 'network-only'
          })
          return result.data.customer
        } catch (err) {
          throw new Error('Authentication Error')
        }
      }
      return undefined
    }
  },
  Mutation: {
    login: async (
      _,
      { input: { email, password, forAccountLinking = false } },
      { client }
    ) => {
      try {
        const resp = await client.mutate({
          mutation: LOGIN_SERVER,
          variables: {
            input: { email, password }
          }
        })
        return handleAuthResponse(resp.data.login, forAccountLinking)
      } catch ({ message }) {
        deleteTokens()
        return { message, __typename: 'LoginError' }
      }
    },
    mfaLogin: async (
      _,
      { input: { code, challengeToken, email, forAccountLinking = false } },
      { client }
    ) => {
      try {
        const resp = await client.mutate({
          mutation: MFA_LOGIN_SERVER,
          variables: {
            input: { code, challengeToken, email }
          }
        })

        return handleAuthResponse(resp.data.mfaLogin, forAccountLinking)
      } catch ({ message }) {
        deleteTokens()
        return { message, __typename: 'LoginError' }
      }
    },
    logout: async (
      _obj,
      { input: { cart, allowBirthdayRewardsRedemption } },
      { client }
    ) => {
      await cleanUpCartData(client, { cart }, allowBirthdayRewardsRedemption)
      deleteTokens()
      client.resetStore()
      return {
        message: 'success!',
        __typename: 'LogoutResponse'
      }
    },
    passwordlessLogin: async (_, { input: { code, source } }, { client }) => {
      try {
        await config.authClient.loginWithCode(code)
        const resp = await client.mutate({
          mutation: COMPLETE_PROFILE_CREATION,
          variables: {
            input: { source }
          }
        })

        const {
          CompleteProfileCreationResponse,
          CompleteProfileCreationError
        } = dataByTypename(resp.data.completeProfileCreation)

        if (CompleteProfileCreationError) {
          throw Error(CompleteProfileCreationError.message)
        }

        return CompleteProfileCreationResponse
      } catch ({ message }) {
        setAuthSessionActive(false)

        return {
          message: getLoginCodeErrorMessage(message),
          __typename: 'CompleteProfileCreationError'
        }
      }
    },
    //v1.5/2.0 login flow = start -> confirm -> complete
    startPasswordlessLogin: async (_, { input }, { client }) => {
      const resp = await client.mutate({
        mutation: START_IDENTITY_PROFILE,
        variables: {
          input
        }
      })

      const { StartIdentityProfileError } = dataByTypename(
        resp.data.startIdentityProfile
      )

      if (StartIdentityProfileError) {
        return StartIdentityProfileError
      }
    },
    confirmPasswordlessLogin: async (
      _,
      { input: { code, source } },
      { client }
    ) => {
      try {
        await config.authClient.loginWithCode(code)
      } catch ({ message }) {
        setAuthSessionActive(false)

        return {
          message: getLoginCodeErrorMessage(message),
          __typename: 'PasswordlessLoginError'
        }
      }
      //valid token is now active
      let resp
      try {
        resp = await client.query({
          query: CUSTOMER_SERVER,
          fetchPolicy: 'network-only'
        })
      } catch (error) {
        // guest profile does not exist or failed to fetch
        // set the auth session to false to disable the token if the user reloads the app
        // do not revoke the token via logout, since we need it for completePasswordlessLogin as the next step
        setAuthSessionActive(false)

        return {
          guestGuid: null,
          profileRequirements: null,
          completed: false,
          __typename: 'ConfirmPasswordlessLoginResponse'
        }
      }

      const guest = resp.data.customer
      const guestGuid = guest.guid
      const profileRequirements = {
        email: guest.email,
        firstName: guest.firstName,
        lastName: guest.lastName
      }

      const completed =
        profileRequirements.email &&
        profileRequirements.firstName &&
        profileRequirements.lastName
      if (completed) {
        //profile is complete, but may be unverified
        //send verification request, do not wait for response
        //it's ok if the request fails, verified profiles are the common case,
        //so don't block the user for the uncommon case
        completeIdentityProfile(client, {
          ...profileRequirements,
          source
        })
      } else {
        setAuthSessionActive(false)
      }

      return {
        guestGuid,
        profileRequirements,
        completed: !!completed,
        __typename: 'ConfirmPasswordlessLoginResponse'
      }
    },
    completePasswordlessLogin: async (_, { input }, { client }) => {
      try {
        // complete identity is an authenticated call so ensure session is active
        // so the token is included and gets refreshes if it expired
        setAuthSessionActive(true)
        const result = await completeIdentityProfile(client, input)

        const {
          CompleteIdentityProfileResponse,
          CompleteIdentityProfileError
        } = dataByTypename(result)

        if (CompleteIdentityProfileError) {
          setAuthSessionActive(false)
          return CompleteIdentityProfileError
        }

        return CompleteIdentityProfileResponse
      } catch (err) {
        setAuthSessionActive(false)
        throw err
      }
    },
    cancelPasswordlessLogin: async () => {
      setAuthSessionActive(true) // indicate there is an active token that needs revoking; logout doesn't work unless this is true
      try {
        await config.authClient.logout() //ensure any active token is cleaned up in the auth client and sets active session to false
      } catch (err) {
        setAuthSessionActive(false)
        throw err
      }
    },
    passwordlessLogout: async (
      _obj,
      { input: { cart, allowBirthdayRewardsRedemption } },
      { client }
    ) => {
      try {
        await config.authClient.logout()
        await cleanUpCartData(client, { cart }, allowBirthdayRewardsRedemption)

        // client.resetStore() doesnt clear out user data
        // and client.clearStore() by itself, clears out the default restaurant data
        // so we need to write the default data back in
        await client.clearStore()
        cache.writeQuery(defaultData)

        //this is done so when a user logs out, the history state is always cleared.
        // we need this because the SmsConfirmationBenefits shows based on the from property on the confirm page
        if (window.history.state?.state?.from) {
          window.history.replaceState(
            { ...window.history.state, state: { from: 'logout ' } },
            ''
          )
        }

        return {
          message: 'success!',
          __typename: 'LogoutResponse'
        }
      } catch ({ message }) {
        return { message, __typeName: 'PasswordlessLogoutError' }
      }
    },
    authenticate: (
      _,
      {
        input: {
          accessToken,
          refreshToken,
          customerGuid,
          forAccountLinking = false
        }
      }
    ) => {
      if (forAccountLinking) {
        setTokens({ accessToken, refreshToken }, accountLinkingStorage)
      } else {
        setTokens({ accessToken, refreshToken })
      }
      return {
        accessToken,
        refreshToken,
        customerGuid,
        __typename: 'AuthenticationResponse'
      }
    }
  }
}
